import { useEffect, useState } from "react";
import { getIndicators, getMeta, getNeedIndexDistrict, getNeedIndexState } from "../lib/api";
import { EvidencePanel } from "../components/EvidencePanel";
import { ResultNumbers } from "../components/ResultNumbers";
import { ErrorBox, Loading, PageHeader } from "../components/Shell";
import { Drawer, EvidenceButton } from "../components/Drawer";
import { CaveatNote, EvidenceSection, GrantRecord, PlaceChip, StatTile, SummaryBanner } from "../components/Evidence";
import { inr } from "../lib/format";
import { Select } from "./PartnersPage";

export default function NeedFundingPage() {
  const [meta, setMeta] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [scope, setScope] = useState("state");
  const [indicator, setIndicator] = useState("children_stunted_pct");
  const [theme, setTheme] = useState("");
  const [data, setData] = useState(null);
  const [evidence, setEvidence] = useState(false);
  const [focus, setFocus] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getMeta().then(setMeta).catch(setErr);
    getIndicators().then((d) => setIndicators(d.indicators)).catch(setErr);
  }, []);

  useEffect(() => {
    setData(null);
    setFocus(null);
    const call =
      scope === "state"
        ? getNeedIndexState({ indicator, theme: theme || undefined })
        : getNeedIndexDistrict({ theme: theme || "health", limit: 60 });
    call.then(setData).catch(setErr);
  }, [scope, indicator, theme]);

  const active = indicators.find((i) => i.key === indicator);

  return (
    <div>
      <PageHeader title="Where standing need outruns our spend"
                  testId="nfi-header"
                  actions={data && <EvidenceButton onClick={() => setEvidence(true)}
                                                   answerability={data.answerability} />} />

      <div className="flex gap-2 flex-wrap mb-6" data-testid="nfi-filters">
        <Select value={scope} onChange={setScope} testId="filter-scope"
                options={[
                  { value: "state", label: "State · published indicators" },
                  { value: "district", label: "District · seeded need indicator" },
                ]} />
        {scope === "state" && (
          <Select value={indicator} onChange={setIndicator} testId="filter-indicator"
                  options={indicators.map((i) => ({ value: i.key, label: `${i.label} (${i.reference_period})` }))} />
        )}
        <Select value={theme} onChange={setTheme} label="Theme from the indicator" testId="filter-theme"
                options={(meta?.themes || []).map((t) => ({ value: t.key, label: t.label }))} />
      </div>

      {active && scope === "state" && (
        <div className="flex flex-wrap gap-x-10 gap-y-3 mb-8 text-[13px]" data-testid="indicator-meta">
          <Fact label="Unit" value={active.unit} />
          <Fact label="Direction" value={active.direction_note} />
          <Fact label="Reference period" value={active.reference_period} />
          <Fact label="Coverage" value={`${active.granularity}`} />
          <Fact label="Themes" value={active.themes.join(", ")} />
        </div>
      )}

      {err && <ErrorBox error={err} />}
      {!data && !err && <Loading />}

      {data && (
        <ResultNumbers result={data}
                       onRowClick={(r) => { setFocus(r); setEvidence(true); }} />
      )}

      <Drawer open={evidence} onClose={() => setEvidence(false)} testId="nfi-evidence-drawer">
        {data && (
          <EvidencePanel result={data} testId="nfi-evidence">
            <div className="space-y-6">
              <SummaryBanner>
                {scope === "state"
                  ? `${data.state_count} states ranked on ${data.indicator_meta?.label || data.indicator}. Bajaj Finserv funds ${data.bajaj_states_funded} of them on ${data.theme_label || "this theme"}, across ${data.bajaj_grants} grants.`
                  : `${data.district_total} districts ranked on ${data.indicator}, against ${data.bajaj_grants} Bajaj Finserv grants.`}
              </SummaryBanner>

              {focus?.bajaj_grant_detail?.length ? (
                <EvidenceSection
                  label={`${focus.state_name} · the grants behind ${inr(focus.bajaj_committed_inr)}`}
                  note={`${data.theme_label || "theme"} only`}
                  testId="nfi-grant-trace"
                >
                  <div className="grid grid-cols-2 gap-2.5">
                    <StatTile label="Aimed at this state"
                              value={inr(focus.bajaj_targeted_inr)}
                              sub={`${focus.bajaj_targeted_grants} grant(s) covering fewer than 6 states`} />
                    <StatTile label="Pan-India share"
                              value={inr(focus.bajaj_apportioned_inr)}
                              sub={focus.bajaj_apportioned_grants
                                ? `even split of ${focus.bajaj_apportioned_grants} grant(s) across up to ${focus.bajaj_max_states_split} states`
                                : "none"} />
                  </div>
                  <div className="space-y-2.5">
                    {focus.bajaj_grant_detail.map((g) => (
                      <GrantRecord key={g.grant_id} grant={g} stateName={focus.state_name}
                                   testId={`grant-record-${g.grant_id}`} />
                    ))}
                  </div>
                  {focus.partners_reporting_presence?.length ? (
                    <div className="pt-1">
                      <div className="text-[12px] text-stone-600 mb-1.5">
                        Partners reporting operations in {focus.state_name}:
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {focus.partners_reporting_presence.slice(0, 12).map((p) => (
                          <PlaceChip key={p.partner}>
                            {p.partner}
                            {p.already_funded_here_on_this_theme ? " · funded here" : " · not yet funded here"}
                          </PlaceChip>
                        ))}
                      </div>
                      <div className="text-[12px] text-stone-600 mt-1.5 leading-relaxed">
                        {focus.partner_presence_source}
                      </div>
                    </div>
                  ) : null}
                </EvidenceSection>
              ) : scope === "state" ? (
                <p className="text-[13px] text-stone-600" data-testid="nfi-trace-hint">
                  Click any state to trace the grants behind its committed figure.
                </p>
              ) : null}

              <CaveatNote title="Which inputs are real">
                The state view runs entirely on published indicators, mGrant commitments and MCA
                peer filings — no placeholder inputs. The district view exists so the pipeline is
                testable end to end; its need indicator is still seeded, which is why it is capped
                at a Directional badge.
              </CaveatNote>
              <CaveatNote>
                A positive index means the geography carries more need on this indicator than its
                share of our committed {data.theme_label || "theme"} portfolio. It is a
                prioritisation signal about standing need, never a statement about the effect of a
                programme — the indicator years and the grant years do not align.
              </CaveatNote>
            </div>
          </EvidencePanel>
        )}
      </Drawer>
    </div>
  );
}

const Fact = ({ label, value }) => (
  <div>
    <div className="text-[12px] text-stone-600">{label}</div>
    <div className="text-stone-800 mt-0.5">{value}</div>
  </div>
);
